import { useCallback, useEffect, useRef, useState } from 'react'

import useNavigationCountdown from './useNavigationCountdown'

/*
 * Der Knopf lässt sich anfassen und verschieben, und er rastet beim Loslassen
 * an der nächsten senkrechten Kante ein.
 *
 * Warum überhaupt: Er liegt fest über dem Seiteninhalt und deckt damit
 * unvermeidlich irgendwo etwas zu. Statt zu raten, wo das am wenigsten stört,
 * darf ihn jede:r dorthin schieben, wo er gerade nicht im Weg ist.
 *
 * Die verschobene Lage gilt **auf Zeit**: Nach fünf Bildschirmwechseln steht
 * der Knopf wieder unten rechts (siehe useNavigationCountdown). Verschoben wird
 * er für den Augenblick — für die Tabelle, die er gerade verdeckt —, und nicht
 * als Einstellung, die man Wochen später an einem anderen Bildschirm sucht.
 */

const RAND = 24
// Erst ab dieser Strecke gilt es als Ziehen. Ohne das würde jeder Klick mit
// zitternder Hand zum Verschieben, und das Fenster ginge nicht auf.
const ZIEH_SCHWELLE = 5

export const POSITION_KEY = 'junior.help.pos'

/** Innerhalb des Fensters halten — auch nach dem Verkleinern des Browsers. */
const einpassen = (pos, groesse) => {
	const maxX = Math.max(RAND, window.innerWidth - groesse.breite - RAND)
	const maxY = Math.max(RAND, window.innerHeight - groesse.hoehe - RAND)
	return {
		x: Math.min(Math.max(pos.x, RAND), maxX),
		y: Math.min(Math.max(pos.y, RAND), maxY)
	}
}

export default function useFabPosition(groesse) {
	/*
	 * Zwei Lagen, und das mit Absicht: `gemerkt` ist die eingerastete Lage, die
	 * der Zähler wieder abräumt; `zug` ist die Lage während des Ziehens. Nur so
	 * schreibt das Ziehen nicht bei jeder Mausbewegung in den Speicher.
	 *
	 * Ohne beides — also ohne Eintrag und ohne Zug — hängt der Knopf per CSS
	 * unten rechts. Bewusst so und nicht als ausgerechnete Startkoordinate: Wird
	 * beim Anzeigen gerechnet, während der Browser die Fenstergröße noch mit 0
	 * angibt (versteckter Tab, Wiederherstellen einer Sitzung), landet der Knopf
	 * oben links und bleibt dort — genau das ist beim ersten Ausprobieren
	 * passiert. Eine Koordinate entsteht erst, wenn jemand ihn wirklich zieht.
	 */
	const { wert: gemerkt, starte, passeAn, beende } = useNavigationCountdown(POSITION_KEY)
	const [zug, setZug] = useState(null)
	const [ziehtGerade, setZiehtGerade] = useState(false)
	const zieh = useRef(null)
	// Die zuletzt gezogene Lage auch als `ref`: Beim Loslassen daraus zu rechnen
	// ist verlässlicher, als es im Zustands-Aktualisierer zu tun — der darf
	// nichts anderes anstoßen.
	const letzteLage = useRef(null)
	/*
	 * Nach dem Ziehen kommt vom Browser noch ein Klick auf denselben Knopf. Ohne
	 * diese Sperre öffnet jedes Verschieben das Formular — der Grund, warum sich
	 * der Knopf nicht vernünftig verschieben ließ. Ein Merker im `ref` und nicht
	 * im Zustand: Der Klick folgt unmittelbar auf das Loslassen, da ist ein neu
	 * gesetzter Zustandswert noch nicht angekommen.
	 */
	const klickSperre = useRef(false)

	const position = zug || (gemerkt ? { x: gemerkt.x, y: gemerkt.y } : null)

	// Beim Verkleinern des Fensters nachziehen, sonst steht der Knopf draußen.
	useEffect(() => {
		const onResize = () => passeAn(prev => einpassen(prev, groesse))
		window.addEventListener('resize', onResize)
		return () => window.removeEventListener('resize', onResize)
	}, [groesse, passeAn])

	/** Wo der Knopf gerade steht — auch, wenn er noch per CSS hängt. */
	const aktuelleLage = element => {
		if (position) return position
		const kasten = element?.getBoundingClientRect()
		return kasten ? { x: kasten.left, y: kasten.top } : { x: 0, y: 0 }
	}

	const onPointerDown = event => {
		if (event.button !== undefined && event.button !== 0) return
		// Ausgangspunkt ist, wo der Knopf tatsächlich steht — beim ersten Ziehen
		// ist das die per CSS gesetzte Ecke.
		const start = aktuelleLage(event.currentTarget)
		zieh.current = {
			startX: event.clientX,
			startY: event.clientY,
			posX: start.x,
			posY: start.y,
			bewegt: false
		}
		event.currentTarget.setPointerCapture?.(event.pointerId)
	}

	const onPointerMove = event => {
		if (!zieh.current) return
		const dx = event.clientX - zieh.current.startX
		const dy = event.clientY - zieh.current.startY

		if (!zieh.current.bewegt && Math.hypot(dx, dy) < ZIEH_SCHWELLE) return

		zieh.current.bewegt = true
		setZiehtGerade(true)
		letzteLage.current = einpassen({ x: zieh.current.posX + dx, y: zieh.current.posY + dy }, groesse)
		setZug(letzteLage.current)
	}

	const onPointerUp = event => {
		const zustand = zieh.current
		zieh.current = null
		setZiehtGerade(false)
		event.currentTarget.releasePointerCapture?.(event.pointerId)

		if (!zustand?.bewegt || !letzteLage.current) return

		// Einrasten: an die nähere senkrechte Kante. Frei schwebend sieht der
		// Knopf nach Versehen aus.
		const gezogen = letzteLage.current
		const mitte = gezogen.x + groesse.breite / 2
		const links = mitte < window.innerWidth / 2
		starte(einpassen({ x: links ? RAND : window.innerWidth - groesse.breite - RAND, y: gezogen.y }, groesse))
		letzteLage.current = null
		setZug(null)

		klickSperre.current = true
		// Falls kein Klick mehr folgt (abgebrochene Zeigergeste), darf die Sperre
		// nicht liegen bleiben: Der Klick kommt noch in diesem Arbeitsschritt,
		// eine Zeitschaltung von 0 danach.
		window.setTimeout(() => {
			klickSperre.current = false
		}, 0)
	}

	/** Meldet, ob dieser Klick nur das Ende eines Ziehens ist. */
	const warZiehen = useCallback(() => {
		if (!klickSperre.current) return false
		klickSperre.current = false
		return true
	}, [])

	return {
		position,
		ziehtGerade,
		/** Zurück an die Ausgangsecke — der Knopf soll dort wieder auftauchen. */
		zuruecksetzen: beende,
		warZiehen,
		handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp }
	}
}
